import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import {
  updateFetcherData,
  useRequest,
} from "@prismicio/editor-support/Suspense"
import { z } from "zod"

import {
  type AuthStrategy,
  throwConfigError,
  useEditorConfig,
} from "../../../EditorConfig"

export function useTagSearch() {
  const config = useEditorConfig()

  if (!config.mediaLibrary) throwConfigError("mediaLibrary")

  const { assetsApiBaseUrl, authStrategy, repository } = config

  const { items } = useRequest(searchTags, [
    assetsApiBaseUrl,
    repository,
    authStrategy,
  ])

  return {
    tags: items,
  }
}

function searchTags(
  baseUrl: URL,
  repository: string,
  authStrategy: AuthStrategy,
): Promise<TagSearchResponse> {
  return request(new URL("tags", baseUrl), {
    credentials: getRequestCredentials(authStrategy),
    okType: tagSearchResponse,
    headers: {
      repository,
    },
  })
}

export const minTagNameLength = 1
export const maxTagNameLength = 20

export const tagNameSchema = z
  .string()
  .min(minTagNameLength)
  .max(maxTagNameLength)

export const tagSchema = z.object({
  id: z.string(),
  name: tagNameSchema,
  uploader_id: z.string().optional(),
  created_at: z.number(),
  last_modified: z.number(),
  count: z.number().optional().default(0),
})

const tagSearchResponse = z.object({
  items: z.array(tagSchema),
})

export type Tag = z.TypeOf<typeof tagSchema>
type TagSearchResponse = z.TypeOf<typeof tagSearchResponse>

type UpdateMediaFunction = (currentItems: Tag[]) => Tag[] | undefined

export function updateTagCache(updateFunction: UpdateMediaFunction) {
  updateFetcherData(searchTags, (body) => {
    const { items } = body

    const newItems = updateFunction(items)
    if (!newItems) return undefined

    return {
      items: newItems,
    }
  })
}
